<template>
  <div class="po-wb">
    <!-- Page head -->
    <div class="wb-head">
      <div class="wb-head__titles">
        <h2 class="wb-title">
          {{ isEdit ? `จัดการใบสั่งซื้อ - ${formData.orderNo}` : 'จัดการใบสั่งซื้อ' }}
        </h2>
        <div class="wb-sub">
          <span
            v-if="formData.brand"
            class="wb-sub__chip">{{ formData.brand }}</span>
          <span class="wb-sub__chip">{{ typeText }}</span>
          <span class="wb-sub__dot">·</span>
          <span>{{ formData.purchaseOrderLines.length }} รายการ</span>
        </div>
      </div>
      <div class="wb-head__actions">
        <span
          class="statuspill"
          :class="`statuspill--${statusPill.variant}`">
          <span class="statuspill__led" />
          {{ statusPill.label }}
        </span>
        <v-btn
          v-if="isEdit && formData.state === 'pending' && enableApprove"
          :disabled="loading"
          small
          icon
          target="_blank"
          :to="`/purchases/orders/${formData.id}/pdf`">
          <v-icon>
            mdi-magnify
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Lock banner -->
    <div
      v-if="!isPending"
      class="lockbanner"
      :class="`lockbanner--${lockBannerInfo.variant}`">
      <v-icon
        size="18"
        class="lockbanner__icon">
        {{ lockBannerInfo.icon }}
      </v-icon>
      <span>{{ lockBannerInfo.text }}</span>
    </div>

    <v-form
      ref="order-form"
      @submit.prevent="onSubmit()">
      <!-- Meta band -->
      <div class="metaband">
        <div class="metaband__cell metaband__cell--vendor">
          <div class="mb-label">
            ข้อมูลผู้ขาย
          </div>
          <div class="vendor">
            <div class="vendor__avatar">
              <v-icon
                size="20"
                color="#ffffff">
                mdi-store-outline
              </v-icon>
            </div>
            <div class="vendor__fields">
              <v-text-field
                v-model="vendorName"
                placeholder="ชื่อผู้ขาย"
                color="secondary"
                :rules="textBoxRules"
                dense
                hide-details="auto"
                readonly
                :disabled="!isPending"
                class="vendor__name" />
              <v-text-field
                v-model="vendorNumber"
                placeholder="รหัสผู้ขาย"
                color="secondary"
                :rules="textBoxRules"
                dense
                hide-details="auto"
                readonly
                :disabled="!isPending"
                class="vendor__no" />
            </div>
            <v-btn
              color="secondary"
              small
              :disabled="!isPending"
              @click="toggleVendor()">
              เลือก
            </v-btn>
          </div>
        </div>
        <div class="metaband__cell">
          <div class="mb-label">
            วันที่เอกสาร
          </div>
          <date-picker
            v-model="formData.documentDate"
            label="วันที่เอกสาร"
            :rules="textBoxRules"
            :disabled="loading || !isPending" />
        </div>
        <div class="metaband__cell">
          <div class="mb-label">
            แบรนด์
          </div>
          <v-select
            v-model="formData.brand"
            color="secondary"
            :rules="textBoxRules"
            :items="productAttributes.brands"
            item-text="name"
            item-value="name"
            label="แบรนด์"
            dense
            outlined
            hide-details="auto"
            :disabled="loading || isEdit" />
        </div>
        <div class="metaband__cell">
          <div class="mb-label">
            คลังสินค้าที่รับ
          </div>
          <v-select
            v-model="formData.locationCode"
            :items="filteredWarehouse"
            :menu-props="{ offsetY: true }"
            item-value="code"
            item-text="label"
            :rules="textBoxRules"
            label="คลังสินค้าที่รับ"
            color="secondary"
            outlined
            dense
            hide-details="auto"
            :disabled="loading || !isPending"
            @change="changeLocationCode" />
        </div>
        <div class="metaband__cell">
          <div class="mb-label">
            ราคารวม VAT
          </div>
          <v-select
            v-model="formData.pricesIncludeTax"
            color="secondary"
            :items="vats"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details="auto"
            :disabled="true" />
        </div>
        <div class="metaband__cell">
          <div class="mb-label">
            สกุลเงิน
          </div>
          <v-select
            v-model="formData.currencyCode"
            placeholder="THB"
            color="secondary"
            :items="currencies"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details="auto"
            :disabled="loading || !isPending" />
        </div>
      </div>

      <!-- Bulk-edit toolbar -->
      <div
        v-if="formData.purchaseOrderLines.length"
        class="ctoolbar">
        <div class="ctoolbar__head">
          <v-icon size="16">
            mdi-flash-outline
          </v-icon>
          แก้ไขทั้งหมด
        </div>
        <div class="ctoolbar__fields">
          <v-text-field
            v-model.number="bulkUpdate.quantity"
            type="number"
            label="จำนวน"
            dense
            outlined
            hide-details
            :disabled="loading || !isPending" />
          <v-text-field
            v-model.number="bulkUpdate.directUnitCost"
            type="number"
            :label="`ราคา/หน่วย (${currencyLabel})`"
            dense
            outlined
            hide-details
            :disabled="loading || !isPending" />
          <v-text-field
            v-model.number="bulkUpdate.discountPercent"
            type="number"
            label="ส่วนลด (%)"
            dense
            outlined
            hide-details
            :disabled="loading || !isPending" />
          <date-picker
            v-model="bulkUpdate.expectedReceiptDate"
            label="วันที่รับสินค้า"
            :disabled="loading || formData.state === 'canceled'" />
        </div>
        <div class="ctoolbar__actions">
          <v-btn
            color="success"
            small
            :disabled="loading"
            @click="applyBulkUpdate()">
            Apply all
          </v-btn>
          <v-btn
            color="error"
            small
            :disabled="loading"
            @click="clearAllLines()">
            Clear all
          </v-btn>
        </div>
      </div>

      <!-- Line-item table -->
      <div
        v-if="formData.purchaseOrderLines.length"
        class="tablewrap">
        <table class="ptable">
          <thead>
            <tr>
              <th class="ptable__prod">
                {{ typeText }}
              </th>
              <th class="num">
                จำนวน
              </th>
              <th class="num">
                ราคา/หน่วย
              </th>
              <th class="num">
                ส่วนลด %
              </th>
              <th class="date">
                วันที่รับ
              </th>
              <th class="num">
                รวม
              </th>
              <th class="act" />
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in formData.purchaseOrderLines">
              <tr
                :key="`line-${index}`"
                class="ptable__row">
                <td class="tprod">
                  <div class="tprod__inner">
                    <img
                      v-if="item.imageURL"
                      :src="$convertImgix(`${item.imageURL}?w=256`)"
                      class="tthumb tthumb--clickable"
                      alt="product-image"
                      @click="openPreview(item.imageURL)">
                    <img
                      v-else
                      :src="noImgAvailable"
                      class="tthumb"
                      alt="product-image">
                    <div class="tprod__meta">
                      <div class="tprod__code">
                        {{ item.sequence || 0 }} : {{ item.itemNo || `${typeText}ใหม่` }}
                      </div>
                      <div class="tprod__desc">
                        <span>{{ item.description }}</span>
                        <span
                          v-if="type !== 'RM' && (item.color || item.size)"
                          class="tprod__variant">{{ item.color }} : {{ item.size }}</span>
                      </div>
                      <div
                        v-if="isEdit"
                        class="tprod__recv">
                        รับแล้ว {{ item.receivedQty || 0 }} / {{ item.quantity }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="cellinput num">
                  <v-text-field
                    v-model.number="item.quantity"
                    :rules="textBoxRules"
                    :suffix="item.uom"
                    color="secondary"
                    type="number"
                    placeholder="Quantity"
                    dense
                    flat
                    solo
                    hide-details="auto"
                    :disabled="loading || !isPending" />
                </td>
                <td class="cellinput num">
                  <v-text-field
                    v-model.number="item.directUnitCost"
                    :rules="numberBoxRules"
                    :suffix="currencyLabel"
                    color="secondary"
                    type="number"
                    placeholder="Unit Price"
                    dense
                    flat
                    solo
                    hide-details="auto"
                    :disabled="loading || !isPending" />
                </td>
                <td class="cellinput num">
                  <v-text-field
                    v-model.number="item.discountPercent"
                    :rules="percentBoxRules"
                    suffix="%"
                    color="secondary"
                    type="number"
                    placeholder="Discount"
                    dense
                    flat
                    solo
                    hide-details="auto"
                    :disabled="loading || !isPending" />
                </td>
                <td class="cellinput date">
                  <date-picker
                    v-model="item.expectedReceiptDate"
                    placeholder="Receipt date"
                    :icon-size="18"
                    :rules="textBoxRules"
                    :disabled="loading || formData.state === 'canceled'" />
                </td>
                <td class="tamt num">
                  {{ formatMoney(lineNet(item)) }}
                </td>
                <td class="act">
                  <v-btn
                    icon
                    small
                    color="error"
                    :disabled="formData.purchaseOrderLines.length <= 1 || !isPending"
                    @click="removeItem(index)">
                    <v-icon size="18">
                      mdi-delete-outline
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr
                v-if="type === 'RM'"
                :key="`bom-${index}`"
                class="ptable__bomrow">
                <td colspan="7">
                  <div class="bomcell">
                    <span class="bomcell__label">สินค้าที่ใช้วัตถุดิบนี้</span>
                    <v-autocomplete
                      v-model="item.useForProducts"
                      :search-input.sync="item.search"
                      color="secondary"
                      :rules="textBoxRules"
                      :items="computeBOMs"
                      chips
                      deletable-chips
                      multiple
                      small-chips
                      dense
                      outlined
                      hide-no-data
                      hide-selected
                      hide-details="auto"
                      :loading="loading || item.searching"
                      :disabled="loading || !isPending"
                      @update:search-input="handleUseForProducts(index)" />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div class="tablefoot">
          <v-btn
            text
            small
            color="secondary"
            :disabled="!isPending"
            @click="toggleItem()">
            <v-icon
              size="16"
              class="mr-1">
              mdi-tshirt-crew-outline
            </v-icon>
            {{ `เพิ่ม${typeText}` }}
          </v-btn>
          <!-- <v-btn
            text
            small
            color="secondary"
            :disabled="!isPending"
            @click="addChargeItem('freight')">
            <v-icon
              size="16"
              class="mr-1">
              mdi-truck
            </v-icon>
            เพิ่มค่าขนส่ง
          </v-btn>
          <v-btn
            text
            small
            color="secondary"
            :disabled="!isPending"
            @click="addChargeItem('labour')">
            <v-icon
              size="16"
              class="mr-1">
              mdi-scissors-cutting
            </v-icon>
            เพิ่มค่าแรง
          </v-btn> -->
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="emptylines">
        <v-icon
          size="32"
          color="#a1a1aa">
          mdi-package-variant
        </v-icon>
        <div class="emptylines__text">
          ยังไม่มีรายการ — เพิ่ม{{ typeText }}เพื่อเริ่มต้น
        </div>
        <v-btn
          color="secondary"
          small
          :disabled="!isPending"
          @click="toggleItem()">
          <v-icon
            size="16"
            class="mr-1">
            mdi-plus
          </v-icon>
          {{ `เพิ่ม${typeText}` }}
        </v-btn>
      </div>

      <!-- Two-up: ship/remark + summary -->
      <div class="twoup">
        <div class="card twoup__left">
          <div class="card__label">
            ที่อยู่จัดส่ง &amp; หมายเหตุ
          </div>
          <v-textarea
            v-model="shipAddress"
            label="ที่อยู่จัดส่ง"
            color="secondary"
            dense
            outlined
            rows="3"
            :rules="[maxLengthRule(100)]"
            :disabled="loading || !isPending" />
          <v-textarea
            v-model="formData.remark"
            label="หมายเหตุ"
            color="secondary"
            dense
            outlined
            rows="2"
            hide-details="auto"
            :disabled="loading || !isPending" />
        </div>
        <div class="card summary">
          <div class="card__label">
            สรุปยอด
          </div>
          <div class="summary__row">
            <span>มูลค่ารวม</span>
            <span class="mono">{{ formatMoney(summary.gross) }}</span>
          </div>
          <div class="summary__row summary__row--neg">
            <span>ส่วนลด</span>
            <span class="mono">- {{ formatMoney(summary.discount) }}</span>
          </div>
          <div class="summary__row">
            <span>ยอดหลังหักส่วนลด</span>
            <span class="mono">{{ formatMoney(summary.subtotal) }}</span>
          </div>
          <div class="summary__row">
            <span>ภาษีมูลค่าเพิ่ม 7%</span>
            <span class="mono">{{ formatMoney(summary.vat) }}</span>
          </div>
          <div class="summary__row summary__row--grand">
            <span>ยอดสุทธิ ({{ currencyLabel }})</span>
            <span class="mono">{{ formatMoney(summary.grand) }}</span>
          </div>
        </div>
      </div>
    </v-form>

    <!-- Sticky action bar -->
    <div class="actionbar">
      <div class="actionbar__total">
        <span class="actionbar__total-label">ยอดสุทธิ</span>
        <span class="actionbar__total-amt mono">{{ formatMoney(summary.grand) }} {{ currencyLabel }}</span>
      </div>
      <div class="actionbar__spacer" />
      <v-btn
        color="secondary"
        :disabled="formData.purchaseOrderLines.length === 0 || formData.state === 'canceled'"
        @click="onSubmit()">
        <v-icon
          size="16"
          class="mr-1">
          mdi-content-save-outline
        </v-icon>
        บันทึก
      </v-btn>
      <template v-if="isEdit">
        <v-btn
          v-if="formData.state === 'pending' && enableApprove"
          color="success"
          :disabled="loading"
          @click="onConfirm('approve')">
          อนุมัติ
        </v-btn>
        <template v-if="formData.state === 'approved' && enableApprove">
          <v-btn
            color="warning"
            :disabled="disableCancel"
            @click="onConfirm('pending')">
            ย้อนสถานะ
          </v-btn>
          <v-btn
            color="error"
            :disabled="disableCancel"
            @click="onConfirm('cancel')">
            ยกเลิก
          </v-btn>
        </template>
        <v-menu
          v-if="formData.deliveryNoteIds.length"
          offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="info"
              :disabled="loading || !['approved', 'received', 'completed'].includes(formData.state)"
              v-bind="attrs"
              v-on="on">
              ดูใบนำเข้า
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in deliveryNoteRef"
              :key="index"
              @click="showDeliveryNote(item.id)">
              <v-list-item-title>
                <span :class="{ 'disabled-text': item.status === 'canceled' }">
                  {{ item.code }}
                </span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          v-if="type === 'FG'"
          color="success"
          :disabled="loading || !['approved', 'received'].includes(formData.state) || !isSync"
          @click="onConfirm('delivery')">
          สร้างใบนำเข้า
        </v-btn>
        <v-btn
          v-else
          color="success"
          :disabled="loading || !['approved', 'received'].includes(formData.state) || !isSync"
          @click="onConfirm('receive')">
          รับสินค้า
        </v-btn>
        <v-btn
          color="teal darken-1"
          dark
          :disabled="loading || !['approved', 'received'].includes(formData.state)"
          @click="onConfirm('complete')">
          Complete
        </v-btn>
        <v-btn
          v-if="formData.state === 'pending' && isEdit"
          color="error"
          :disabled="loading"
          @click="onConfirm('delete')">
          <v-icon size="16">
            mdi-delete
          </v-icon>
          <span>
            ลบใบสั่งซื้อ
          </span>
        </v-btn>
      </template>
    </div>

    <v-dialog
      v-model="previewDialog"
      max-width="640">
      <v-card class="po-preview">
        <div class="po-preview__head">
          <v-btn
            icon
            small
            @click="previewDialog = false">
            <v-icon size="18">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
        <img
          :src="previewURL ? $convertImgix(previewURL) : noImgAvailable"
          class="po-preview__img"
          alt="preview">
      </v-card>
    </v-dialog>

    <vendor-list-modal
      v-model="vendorDialog"
      @select="selectVendor($event)"
      @close="toggleVendor()" />
    <item-list-modal
      v-model="itemDialog"
      :type="type"
      :selected-items="formData.purchaseOrderLines"
      :disabled="loading"
      @select="onSelectItem($event)"
      @remove="onRemoveItem($event)"
      @close="toggleItem()" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import getImageOrGradientCss from '@/assets/js/GetImageOrGradientCss'
import { getRole } from '@/assets/js/Authentication'
import NoImgAvailable from '@/assets/image/no_picture_available.png'
import PurchaseOrdersProvider from '@/resources/PurchaseOrdersProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import PurchaseItemsProvider from '@/resources/PurchaseItemsProvider'
import WarehouseProvider from '@/resources/WarehouseProvider'
import UnitOfMeasuresProvider from '@/resources/UnitOfMeasuresProvider'
import PrototypeProvider from '@/resources/PrototypeProvider'
import InventoryProvider from '@/resources/InventoryProvider'
import AssemblyBOMProvider from '@/resources/AssemblyBOMProvider'
import DatePicker from '../components/DatePicker.vue'
import VendorListModal from '../components/VendorListModal.vue'
import ItemListModal from '../components/ItemListModal.vue'

const InventoryService = new InventoryProvider()
const PurchaseItemsService = new PurchaseItemsProvider()
const PurchaseOrdersService = new PurchaseOrdersProvider()
const ProductAttributeService = new ProductAttributeProvider()
const WarehouseService = new WarehouseProvider()
const UnitOfMeasuresService = new UnitOfMeasuresProvider()
const PrototypeService = new PrototypeProvider()
const AssemblyBOMService = new AssemblyBOMProvider()

const WAREHOUSE_LABELS = {
  'GW-FACTORY': 'GW FACTORY',
  'MM-FACTORY': 'MM FACTORY',
  'GW-WHS': 'GW WAREHOUSE',
  'MM-WHS': 'MM WAREHOUSE',
  'GW-XPT': 'GW EXPORT',
  'MM-XPT': 'MM EXPORT'
}

export default {
  components: { VendorListModal, DatePicker, ItemListModal },
  data () {
    return {
      loading: false,
      isEdit: false,
      warehouses: [],
      textBoxRules: [
        (v) => !!v || 'This field is require'
      ],
      numberBoxRules: [
        (v) => (v !== '' && !Number.isNaN(v) && v > 0) || 'This field has an invalid'
      ],
      percentBoxRules: [
        (v) => (v !== '' && !Number.isNaN(v) && v >= 0 && v <= 100) || 'This field has an invalid'
      ],
      UOMs: [],
      BOMs: [],
      bomSearch: '',
      productAttributes: {
        brands: [],
        categories: [],
        colors: [],
        size: [],
        tags: [],
        collections: [],
        groupCategories: []
      },
      currencies: [
        { value: '', text: 'THB' },
        { value: 'CNY-S', text: 'CNY' },
        // { value: 'EUR-S', text: 'EUR' },
        { value: 'MYR-S', text: 'MYR' },
        { value: 'USD-S', text: 'USD' }
      ],
      vats: [
        { value: true, text: 'รวม' },
        { value: false, text: 'ไม่รวม' }
      ],
      formData: {
        documentDate: '',
        dueDate: null,
        vendor: '',
        yourReference: '',
        deliveryNoteIds: [],
        currencyCode: '',
        pricesIncludeTax: false,
        discountAmount: 0,
        noOfLines: 1,
        locationCode: '',
        shipAddress: '',
        shipAddress2: '',
        approver: '',
        approvedDate: '',
        confirmGoodsReceiptNo: '',
        receiver: '',
        receivedDate: '',
        remark: '',
        type: 'FG',
        purchaseOrderLines: [],
        brand: '',
        state: 'pending',
        poStatus: 'Wait',
        poClosed: false,
        bcSync: {
          sync: false,
          bcLog: null
        }
      },
      memoFormData: null,
      bulkUpdate: {
        quantity: null,
        directUnitCost: null,
        discountPercent: null,
        expectedReceiptDate: ''
      },
      deliveryNoteRef: [],
      selectedVendor: null,
      lineTypes: [
        'Item',
        'Resource',
        'Charge (Item)'
      ],
      vendorDialog: false,
      itemDialog: false,
      noImgAvailable: NoImgAvailable,
      previewDialog: false,
      previewURL: ''
    }
  },
  computed: {
    ...mapGetters({
      mapWarehouses: 'Store/mapWarehouse'
    }),
    filteredWarehouse () {
      const POWarehouse = this.type === 'RM'
        ? [
          'GW-FACTORY',
          'MM-FACTORY'
        ] : [
          'GW-WHS',
          'GW-XPT',
          'MM-WHS',
          'MM-XPT'
        ]

      return this.mapWarehouses
        .filter((wh) => POWarehouse.includes(wh.code))
        .map((wh) => ({ ...wh, label: `${WAREHOUSE_LABELS[wh.code] || wh.code} (${wh.code})` }))
    },
    enableApprove () {
      const role = getRole()
      const setCountRoles = [
        'management',
        'developer',
        'accounting_manager',
        'merchandising_planner',
        'merchandiser',
        'support'
      ]
      return setCountRoles.some((r) => r === role) && !this.formData.poClosed
    },
    vendorNumber () {
      return this.selectedVendor?.vendorNo || ''
    },
    vendorName () {
      return this.selectedVendor?.name || ''
    },
    vendorInvoiceNumber () {
      return this.selectedVendor?.taxRegistrationNumber || ''
    },
    isPending () {
      return this.formData.state === 'pending'
    },
    type () {
      return this.$route.query.type || 'RM'
    },
    duplicateId () {
      return this.$route.query.duplicate || ''
    },
    typeText () {
      return this.type === 'FG' ? 'สินค้า' : 'วัตถุดิบ'
    },
    isSync () {
      return this.formData.bcSync?.sync && this.formData.bcSync?.bcLog
    },
    disableCancel () {
      return this.loading
        || !this.isEdit
        || this.deliveryNoteRef.filter((dn) => dn.status !== 'canceled').length > 0
        || this.formData.poStatus !== 'Open'
    },
    shipAddress: {
      get () {
        return [this.formData.shipAddress, this.formData.shipAddress2].join('')
      },
      set (val) {
        this.formData.shipAddress = val.slice(0, 100)
        this.formData.shipAddress2 = val.slice(100, 150)
      }
    },
    computeBOMs () {
      const items = this.formData.purchaseOrderLines.reduce((arr, v) => (v.useForProducts ? [...arr, ...v.useForProducts] : arr), [])

      return [...new Set([...items, ...this.BOMs])]
    },
    currencyLabel () {
      return this.currencies.find((v) => v.value === this.formData.currencyCode)?.text || this.formData.currencyCode
    },
    statusPill () {
      const map = {
        pending: { label: 'รออนุมัติ', variant: 'info' },
        approved: { label: 'อนุมัติแล้ว', variant: 'success' },
        received: { label: 'รับเข้าบางส่วน', variant: 'received' },
        completed: { label: 'รับสำเร็จ', variant: 'completed' },
        canceled: { label: 'ยกเลิก', variant: 'error' }
      }
      return map[this.formData.state] || { label: this.formData.state, variant: 'muted' }
    },
    lockBannerInfo () {
      const map = {
        canceled: {
          icon: 'mdi-cancel',
          text: 'ใบสั่งซื้อนี้ถูกยกเลิกแล้ว ไม่สามารถแก้ไขได้',
          variant: 'canceled'
        },
        received: {
          icon: 'mdi-truck-check-outline',
          text: 'ใบสั่งซื้อนี้ได้รับสินค้าบางส่วนแล้ว แก้ไขได้เฉพาะวันที่รับสินค้า',
          variant: 'received'
        },
        completed: {
          icon: 'mdi-check-circle-outline',
          text: 'ใบสั่งซื้อนี้ได้รับสินค้าครบแล้ว แก้ไขได้เฉพาะวันที่รับสินค้า',
          variant: 'completed'
        }
      }
      return map[this.formData.state] || {
        icon: 'mdi-lock-outline',
        text: 'ใบสั่งซื้อนี้ได้รับการอนุมัติแล้ว แก้ไขได้เฉพาะวันที่รับสินค้า',
        variant: 'approved'
      }
    },
    summary () {
      let gross = 0
      let subtotal = 0
      for (const line of this.formData.purchaseOrderLines) {
        const lineGross = (Number(line.quantity) || 0) * (Number(line.directUnitCost) || 0)
        gross += lineGross
        subtotal += lineGross * (1 - (Number(line.discountPercent) || 0) / 100)
      }
      const vat = subtotal * 0.07
      return {
        gross,
        discount: gross - subtotal,
        subtotal,
        vat,
        grand: subtotal + vat
      }
    },
    isDeveloper () {
      return getRole() === 'developer'
    }
  },
  async mounted () {
    await Promise.all([this.getAllAttributes(), this.getAllWarehouses()])

    if (this.$route.params?.id) {
      this.isEdit = true
      await this.getItem()
      await this.getDeliveryNoteRef()
    } else if (this.duplicateId) {
      await this.getItem(true)
    } else {
      this.formData.type = this.type
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setLoading: 'Components/setLoading',
      setModal: 'Components/setModal'
    }),
    handleUseForProducts (index) {
      clearTimeout(this.formData.purchaseOrderLines[index].searchTimeout)

      this.formData.purchaseOrderLines[index].searchTimeout = setTimeout(() => {
        this.getBOMs(index)
      }, 300)
    },
    async getDeliveryNoteRef () {
      this.loading = true
      try {
        const { data } = await InventoryService.getDeliveryNoteCodes({ ids: this.formData.deliveryNoteIds })
        this.deliveryNoteRef = data.map((d) => ({ id: d.id, code: d.code, status: d.status }))
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      }
      this.loading = false
    },
    changeLocationCode (val) {
      const location = this.warehouses.find((wh) => wh.code === val)
      this.formData.shipAddress = `${location.address} ${location.address2}`
      this.formData.shipAddress2 = `${location.subDistrict} ${location.district} ${location.stateProvince} ${location.postcode}`
    },
    maxLengthRule (length) {
      return (v) => v.length <= length || `Field นี้ต้องยาวไม่เกิน ${length} ตัวอักษร`
    },
    lineNet (item) {
      const gross = (Number(item.quantity) || 0) * (Number(item.directUnitCost) || 0)
      return gross * (1 - (Number(item.discountPercent) || 0) / 100)
    },
    formatMoney (value) {
      return (Number(value) || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    toggleVendor () {
      this.vendorDialog = !this.vendorDialog
    },
    toggleItem () {
      this.itemDialog = !this.itemDialog
    },
    async addChargeItem (chargeItemType) {
      const item = await this.getFixItem(chargeItemType)
      if (!item) {
        this.setSnackbar({
          value: true,
          message: `Item has purchaseItemType = "${chargeItemType}" not found.`,
          type: 'error'
        })
        return
      }
      this.formData.purchaseOrderLines.push({
        sequence: (this.formData.purchaseOrderLines.length + 1) * 10000,
        locationCode: '',
        quantity: 1,
        discountAmount: 0,
        discountPercent: 0,
        ifStatus: '',
        useForProducts: [],
        expectedReceiptDate: '',
        lineType: 'Item',
        itemType: 'RM',
        itemNo: item.itemNo,
        imageURL: item.imageURL,
        description: item.description,
        description2: item.description2.slice(0, 50),
        uom: item.baseUOM || item.salesUOM || item.purchaseUOM || '',
        directUnitCost: item.unitPrice,
        groupCategory: '',
        size: '',
        color: '',
        categories: '',
        model: 'NONE',
        gwCollection: '',
        brand: '',
        search: '',
        searching: false,
        searchTimeout: null
      })
    },
    async getFixItem (purchaseItemType) {
      try {
        this.loading = true
        const { data } = await PurchaseItemsService.getItems({
          purchaseItemType,
          search: '',
          page: 1,
          limit: 1
        })
        return data?.results?.[0] || null
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
        return null
      } finally {
        this.loading = false
      }
    },
    removeItem (index) {
      this.formData.purchaseOrderLines.splice(index, 1)
    },
    isBulkFilled (v) {
      return v !== '' && v !== null && !Number.isNaN(v)
    },
    applyBulkUpdate () {
      this.formData.purchaseOrderLines.forEach((_, i) => {
        const lines = this.formData.purchaseOrderLines
        if (this.isPending) {
          if (this.isBulkFilled(this.bulkUpdate.quantity)) lines[i].quantity = this.bulkUpdate.quantity
          if (this.isBulkFilled(this.bulkUpdate.directUnitCost)) lines[i].directUnitCost = this.bulkUpdate.directUnitCost
          if (this.isBulkFilled(this.bulkUpdate.discountPercent)) lines[i].discountPercent = this.bulkUpdate.discountPercent
        }
        if (this.formData.state !== 'canceled' && this.isBulkFilled(this.bulkUpdate.expectedReceiptDate)) {
          lines[i].expectedReceiptDate = this.bulkUpdate.expectedReceiptDate
        }
      })
    },
    clearAllLines () {
      this.setModal({
        value: true,
        title: 'Clear all lines',
        message: 'Do you want to clear all line values?',
        confirmText: 'Sure',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.doClearAllLines()
      })
    },
    doClearAllLines () {
      this.formData.purchaseOrderLines.forEach((_, i) => {
        const lines = this.formData.purchaseOrderLines
        if (this.isPending) {
          lines[i].quantity = null
          lines[i].directUnitCost = null
          lines[i].discountPercent = 0
        }
        if (this.formData.state !== 'canceled') {
          lines[i].expectedReceiptDate = null
        }
      })
    },
    getColor (arr, itemsDisabled) {
      return arr.map((color) => ({
        ...color,
        label: color.name,
        children: color.childs && color.childs.length > 0 ? this.getColor(color.childs, itemsDisabled) : undefined,
        gradientCss: getImageOrGradientCss(color)
      }))
    },
    async getAllWarehouses () {
      try {
        this.loading = true

        const { data } = await WarehouseService.getWarehouse()
        this.warehouses = data
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getAllAttributes () {
      try {
        this.loading = true
        this.setLoading({ active: true })

        const { data } = await ProductAttributeService.getAllProductAttribute()

        this.productAttributes = {
          colors: Array.isArray(data.colors) ? this.getColor(data.colors) : [],
          brands: Array.isArray(data.brands) ? data.brands : [],
          size: Array.isArray(data.size) ? data.size : [],
          tags: Array.isArray(data.tags) ? data.tags : [],
          categories: Array.isArray(data.categories) ? data.categories : [],
          collections: Array.isArray(data.collections) ? [{ id: null, name: 'ไม่ระบุ' }, ...data.collections] : [],
          groupCategories: Array.isArray(data.groupCategories) ? data.groupCategories : []
        }

        const { data: uom } = await UnitOfMeasuresService.getItems({
          page: 1,
          limit: 9999
        })

        this.UOMs = uom.results.map((r) => ({
          text: r.name,
          value: r.code
        }))
      } catch (error) {
        console.error('getAllAttributes', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async getItem (isDuplicate = false) {
      try {
        this.loading = true
        this.setLoading({ active: true })

        const id = isDuplicate ? this.duplicateId : this.$route.params.id
        const { data } = await PurchaseOrdersService.getItemById(id)

        this.selectedVendor = data.vendor
        this.formData = isDuplicate ? {
          ...data,
          id: null,
          state: 'pending',
          vendor: data.vendor.id,
          deliveryNoteIds: []
        } : {
          ...data,
          vendor: data.vendor.id,
          deliveryNoteIds: data.deliveryNoteId ? [
            ...data.deliveryNoteIds,
            data.deliveryNoteId
          ] : data.deliveryNoteIds
        }

        this.memoFormData = JSON.parse(JSON.stringify(this.formData))
      } catch (error) {
        console.error('getItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async getBOMs (index) {
      try {
        this.formData.purchaseOrderLines[index].searching = true

        const { data } = await AssemblyBOMService.getItems({
          page: 1,
          limit: 20,
          search: this.bomSearch
        })

        this.BOMs = data.results.map((v) => v.parentItemNo)
      } catch (error) {
        console.error('getBOMs', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.formData.purchaseOrderLines[index].searching = false
      }
    },
    onConfirm (type) {
      if (type === 'approve') {
        this.setModal({
          value: true,
          title: 'Approve',
          message: 'Do you want to approve this purchase order',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onApprove()
        })
      } else if (type === 'receive') {
        this.setModal({
          value: true,
          title: 'Receive',
          message: 'Do you want to receive this purchase order',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onReceive()
        })
      } else if (type === 'cancel') {
        this.setModal({
          value: true,
          title: 'Cancel',
          message: 'Do you want to cancel this purchase order',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onCancel()
        })
      } else if (type === 'delete') {
        this.setModal({
          value: true,
          title: 'Delete',
          message: 'Do you want to delete this purchase order',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onDelete()
        })
      } else if (type === 'delivery') {
        this.setModal({
          value: true,
          title: 'Create delivery note',
          message: 'Do you want to create delivery note from this purchase order',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onCreateDelivery()
        })
      } else if (type === 'pending') {
        this.setModal({
          value: true,
          title: 'Reset state to "Pending"',
          message: 'Do you want to reset state this purchase order?',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onResetState()
        })
      } else if (type === 'complete') {
        this.setModal({
          value: true,
          title: 'Complete',
          message: 'Do you want to mark this purchase order as completed',
          confirmText: 'Sure',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'Cancel',
          onConfirm: () => this.onComplete()
        })
      }
    },
    showDeliveryNote (noteId) {
      const warehouse = this.warehouses.find((w) => w.code === this.formData.locationCode)
      const routeData = this.$router.resolve({
        name: 'DeliveryNote',
        query: {
          id: noteId,
          warehouse: warehouse.id
        }
      })
      window.open(routeData.href, '_blank')
    },
    async onApprove () {
      const validated = await this.$refs['order-form'].validate()

      if (!validated) {
        return
      }

      try {
        this.loading = true
        this.setLoading({ active: true })
        this.formData.noOfLines = this.formData.purchaseOrderLines.length
        this.formData.purchaseOrderLines = this.formData.purchaseOrderLines.map((item, index) => ({
          ...item,
          sequence: (index + 1) * 10000,
          locationCode: this.formData.locationCode,
          ifStatus: (index + 1) === this.formData.purchaseOrderLines.length ? 'COMPLETE' : ''
        }))

        await PurchaseOrdersService.updateItem(this.formData)
        await PurchaseOrdersService.approveItem(this.formData.id)
        this.setSnackbar({
          value: true,
          message: 'แก้ไขใบสั่งซื้อสำเร็จ',
          type: 'success'
        })

        this.getItem()
      } catch (error) {
        console.error('onApprove', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async onReceive () {
      const validated = await this.$refs['order-form'].validate()

      if (!validated) {
        return
      }

      try {
        this.loading = true
        this.setLoading({ active: true })

        await PurchaseOrdersService.receiveItem(this.formData.id)
        this.setSnackbar({
          value: true,
          message: 'แก้ไขใบสั่งซื้อสำเร็จ',
          type: 'success'
        })

        this.getItem()
      } catch (error) {
        console.error('onReceive', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async onComplete () {
      try {
        this.loading = true
        this.setLoading({ active: true })

        await PurchaseOrdersService.completeItem(this.formData.id)
        this.setSnackbar({
          value: true,
          message: 'เปลี่ยนสถานะ PO เป็น Completed (รับสำเร็จ) แล้ว',
          type: 'success'
        })

        this.getItem()
      } catch (error) {
        console.error('onComplete', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async onCancel () {
      const validated = await this.$refs['order-form'].validate()

      if (!validated) {
        return
      }

      try {
        this.loading = true
        this.setLoading({ active: true })

        await PurchaseOrdersService.cancelItem(this.formData.id)
        this.setSnackbar({
          value: true,
          message: 'ยกเลิกใบสั่งซื้อสำเร็จ',
          type: 'success'
        })

        if (this.formData.type === 'FG') {
          this.$router.push({ name: 'PurchasesFinishedGoodsOrders' })
        } else {
          this.$router.push({ name: 'PurchasesOrders' })
        }
      } catch (error) {
        console.error('onCancel', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async onDelete () {
      if (this.formData.state !== 'pending') {
        return
      }

      try {
        this.loading = true
        this.setLoading({ active: true })

        await PurchaseOrdersService.deleteItem(this.formData.id)
        this.setSnackbar({
          value: true,
          message: 'ลบใบสั่งซื้อสำเร็จ',
          type: 'success'
        })

        if (this.formData.type === 'FG') {
          this.$router.push({ name: 'PurchasesFinishedGoodsOrders' })
        } else {
          this.$router.push({ name: 'PurchasesOrders' })
        }
      } catch (error) {
        console.error('onDelete', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    async onResetState () {
      // const validated = await this.$refs['order-form'].validate()

      // if (!validated) {
      //   return
      // }

      try {
        this.loading = true
        this.setLoading({ active: true })

        await PurchaseOrdersService.updateItem({
          ...this.formData,
          state: 'pending'
        })

        this.setSnackbar({
          value: true,
          message: 'แก้ไขใบสั่งซื้อสำเร็จ',
          type: 'success'
        })

        this.getItem()
      } catch (error) {
        console.error('onResetState', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    onCreateDelivery () {
      const warehouse = this.warehouses.find((w) => w.code === this.formData.locationCode)
      const routeData = this.$router.resolve({
        name: 'DeliveryNote',
          query: {
          poNo: this.formData.orderNo,
          warehouse: warehouse.id
        }
      })
      window.open(routeData.href, '_blank')
    },
    async onSubmit () {
      const validated = await this.$refs['order-form'].validate()

      if (!validated) {
        return
      }

      try {
        this.loading = true
        this.setLoading({ active: true })

        this.formData.noOfLines = this.formData.purchaseOrderLines.length
        this.formData.purchaseOrderLines = this.formData.purchaseOrderLines.map((item, index) => ({
          ...item,
          sequence: (index + 1) * 10000,
          locationCode: this.formData.locationCode,
          ifStatus: (index + 1) === this.formData.purchaseOrderLines.length ? 'COMPLETE' : ''
        }))

        if (this.isEdit && ['approved', 'received', 'completed'].includes(this.formData.state)) {
          const expectedReceiptDateLines = this.formData.purchaseOrderLines
            .filter((v) => {
              const poLine = this.memoFormData.purchaseOrderLines.find((p) => p.sequence === v.sequence)

              return v.expectedReceiptDate !== poLine.expectedReceiptDate
            })
            .map((v) => ({
              poLineNo: v.sequence,
              expectedReceiptDate: v.expectedReceiptDate
            }))

          if (expectedReceiptDateLines.length) {
            await PurchaseOrdersService.updateExpectedReceiptDates({
              id: this.formData.id,
              expectedReceiptDateLines
            })

            this.setSnackbar({
              value: true,
              message: 'แก้ไขใบสั่งซื้อสำเร็จ',
              type: 'success'
            })

            this.getItem()
          } else {
            this.$router.push({ name: 'PurchasesFinishedGoodsOrders' })
          }
        } else if (this.isEdit) {
          await PurchaseOrdersService.updateItem(this.formData)
          this.setSnackbar({
            value: true,
            message: 'แก้ไขใบสั่งซื้อสำเร็จ',
            type: 'success'
          })

          this.getItem()
        } else {
          const { data } = await PurchaseOrdersService.createItem(this.formData)

          this.setSnackbar({
            value: true,
            message: 'สร้างใบสั่งซื้อสำเร็จ',
            type: 'success'
          })

          this.$router.replace(`/purchases/orders/${data.id}?type=${this.type}`)

          setTimeout(() => window.location.reload(), 500)
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
        this.loading = false
      }
    },
    selectVendor (vendor) {
      this.formData.vendor = vendor.id
      this.formData.currencyCode = vendor?.currencyCode || this.formData.currencyCode
      this.selectedVendor = { ...vendor }
      this.currencyCode = vendor.currencyCode
      this.toggleVendor()
    },
    async onSelectItem (item) {
      if (this.type === 'FG') {
        const codes = item.code.split('.')
        const model = `${codes[0]}.${codes[4]}.${codes[5]}`
        let { price } = item
        let quantity = null

        try {
          this.loading = true
          const { data: prototype } = await PrototypeService.getPrototypeByProductId(`${codes[0]}.${codes[1]}.${codes[2]}.${codes[3]}.${codes[4]}`)

          price = prototype?.order?.manufacCost ?? item.price

          const sku = (prototype?.order?.productSkus || []).find((s) => s.color === item.color.name && s.size === item.size.name)

          quantity = sku?.quantity || null
        } catch (error) {
          console.error('onSelectItem', error)
        } finally {
          this.loading = false
        }

        this.formData.purchaseOrderLines.push({
          sequence: (this.formData.purchaseOrderLines.length + 1) * 10000,
          lineType: 'Item',
          itemType: 'FG',
          itemNo: item.code,
          imageURL: item.imageURL,
          description: item.name,
          description2: item.name.slice(0, 50),
          uom: 'PCS',
          directUnitCost: price,
          groupCategory: item.groupCategories[0],
          size: item.size.name,
          color: item.color.name,
          categories: item.categories[0],
          model,
          gwCollection: item.gwCollection,
          brand: item.brand,
          useForProducts: [],
          expectedReceiptDate: null,
          locationCode: '',
          quantity,
          discountAmount: 0,
          discountPercent: 0,
          ifStatus: '',
          search: '',
          searching: false,
          searchTimeout: null
        })
      } else {
        this.formData.purchaseOrderLines.push({
          sequence: (this.formData.purchaseOrderLines.length + 1) * 10000,
          lineType: 'Item',
          itemType: 'RM',
          itemNo: item.itemNo,
          imageURL: item.imageURL,
          description: item.description,
          description2: item.description2.slice(0, 50),
          uom: item.baseUOM || item.salesUOM || item.purchaseUOM || '',
          directUnitCost: item.unitPrice,
          groupCategory: item.groupCategory,
          size: item.size,
          color: item.color,
          categories: item.categories,
          model: 'NONE',
          gwCollection: item.gwCollection,
          brand: item.brand,
          useForProducts: [],
          expectedReceiptDate: null,
          locationCode: '',
          quantity: null,
          discountAmount: 0,
          discountPercent: 0,
          ifStatus: '',
          search: '',
          searching: false,
          searchTimeout: null
        })
      }
    },
    onRemoveItem (item) {
      const index = this.formData.purchaseOrderLines.findIndex((v) => v.itemNo === item.itemNo)

      if (index !== -1) {
        this.formData.purchaseOrderLines.splice(index, 1)
      }
    },
    openPreview (url) {
      if (!url) {
        return
      }
      this.previewURL = url
      this.previewDialog = true
    }
  }
}
</script>

<style scoped>
.po-wb {
  --ink: #18181b;
  --ink-soft: #3f3f46;
  --muted: #71717a;
  --muted-2: #a1a1aa;
  --line: #e4e4e7;
  --line-strong: #d4d4d8;
  --surface: #ffffff;
  --surface-2: #fafafa;
  --surface-3: #f4f4f5;
  --bg: #ececed;
  --success: #1f8a4c;
  --error: #e03b3b;
  --warning: #c97a16;
  --info: #2563c9;
  --completed: #0f766e;
  --success-bg: #e8f5ee;
  --error-bg: #fdeaea;
  --warning-bg: #fbf1e2;
  --info-bg: #e8f0fc;
  --completed-bg: #e6f4f2;
  --r-sm: 6px;
  --r: 9px;
  --r-lg: 14px;
  position: relative;
  background: var(--bg);
  border-radius: var(--r-lg);
  padding: 20px 20px 16px;
  color: var(--ink);
}

.disabled-text {
  color: #9b9b9b;
}

.mono {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

/* Page head */
.wb-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.wb-title {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
}
.wb-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 13px;
  color: var(--muted);
}
.wb-sub__chip {
  background: var(--surface-3);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 1px 10px;
  font-weight: 500;
  color: var(--ink-soft);
}
.wb-sub__dot {
  color: var(--muted-2);
}
.wb-head__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Status pill */
.statuspill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.statuspill__led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.statuspill--info {
  color: var(--info);
  background: var(--info-bg);
  border-color: rgba(37, 99, 201, 0.18);
}
.statuspill--success {
  color: var(--success);
  background: var(--success-bg);
  border-color: rgba(31, 138, 76, 0.18);
}
.statuspill--error {
  color: var(--error);
  background: var(--error-bg);
  border-color: rgba(224, 59, 59, 0.18);
}
.statuspill--muted {
  color: var(--muted);
  background: var(--surface-3);
  border-color: var(--line);
}
.statuspill--received {
  color: var(--warning);
  background: var(--warning-bg);
  border-color: rgba(201, 122, 22, 0.18);
}
.statuspill--completed {
  color: var(--completed);
  background: var(--completed-bg);
  border-color: rgba(15, 118, 110, 0.18);
}

/* Lock banner */
.lockbanner {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--warning);
  background: var(--warning-bg);
  border: 1px solid rgba(201, 122, 22, 0.22);
  border-radius: var(--r);
  padding: 10px 14px;
  margin-bottom: 16px;
}
.lockbanner__icon {
  color: inherit !important;
}
.lockbanner--canceled {
  color: var(--error);
  background: var(--error-bg);
  border-color: rgba(224, 59, 59, 0.22);
}
.lockbanner--received {
  color: var(--info);
  background: var(--info-bg);
  border-color: rgba(37, 99, 201, 0.22);
}
.lockbanner--completed {
  color: var(--completed);
  background: var(--completed-bg);
  border-color: rgba(15, 118, 110, 0.22);
}

/* Meta band */
.metaband {
  display: grid;
  grid-template-columns: 1.5fr repeat(5, 1fr);
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  margin-bottom: 16px;
}
.metaband__cell {
  background: var(--surface);
  padding: 12px 14px;
}
.metaband__cell--vendor {
  grid-column: span 1;
}
.mb-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 8px;
}
.vendor {
  display: flex;
  align-items: center;
  gap: 12px;
}
.vendor__avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.vendor__fields {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
/* Strip Vuetify chrome so the readonly fields read as a display card.
   The fields keep their :rules, so validate() still gates vendor selection. */
.vendor__fields ::v-deep .v-input {
  margin: 0;
  padding: 0;
}
.vendor__fields ::v-deep .v-input__slot {
  box-shadow: none !important;
  background: transparent !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
.vendor__fields ::v-deep .v-input__slot::before,
.vendor__fields ::v-deep .v-input__slot::after {
  display: none !important;
}
.vendor__name ::v-deep input {
  font-weight: 600;
  font-size: 15px;
  color: var(--ink);
}
.vendor__no ::v-deep input {
  font-size: 12px;
  color: var(--muted);
}

/* Bulk toolbar */
.ctoolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 12px 14px;
  margin-bottom: 14px;
}
.ctoolbar__head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-soft);
  white-space: nowrap;
}
.ctoolbar__fields {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
  min-width: 240px;
}
.ctoolbar__fields > * {
  width: 168px;
  flex: none;
}
.ctoolbar__actions {
  display: flex;
  gap: 8px;
  padding-bottom: 2px;
}

/* Table */
.tablewrap {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: auto;
  max-height: 460px;
  margin-bottom: 16px;
}
.tablefoot {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-top: 1px solid var(--line);
  background: var(--surface);
}
.ptable {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 780px;
}
.ptable thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--surface-2);
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--muted);
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}
.ptable th.num,
.ptable td.num {
  text-align: right;
}
.ptable th.act {
  width: 48px;
}
.ptable th.date {
  width: 160px;
}
.ptable__row td {
  padding: 6px 12px;
  border-bottom: 1px solid var(--line);
  vertical-align: middle;
}
.ptable__row:hover td {
  background: var(--surface-2);
}

.tprod {
  min-width: 240px;
}
.tprod__inner {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tthumb {
  width: 40px;
  height: 40px;
  border-radius: var(--r-sm);
  object-fit: cover;
  background: var(--surface-3);
  border: 1px solid var(--line);
  flex: none;
}
.tthumb--clickable {
  cursor: pointer;
}
.tprod__meta {
  min-width: 0;
}
.tprod__code {
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}
.tprod__desc {
  font-size: 12px;
  color: var(--muted);
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.tprod__variant {
  color: var(--ink-soft);
  font-weight: 500;
}
.tprod__recv {
  font-size: 11px;
  font-weight: 600;
  color: var(--info);
  margin-top: 2px;
}
.tamt {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* BOM sub-row */
.ptable__bomrow td {
  padding: 0 12px 10px 62px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-2);
}
.bomcell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}
.bomcell__label {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
}

/* Flat table-cell inputs */
.cellinput {
  padding: 2px 6px !important;
}
.cellinput ::v-deep .v-input__slot {
  box-shadow: none !important;
  background: transparent !important;
  min-height: 34px !important;
  padding: 0 6px !important;
  border-radius: var(--r-sm);
}
.cellinput ::v-deep .v-input__slot:hover {
  background: var(--surface-3) !important;
}
.cellinput.num ::v-deep input {
  text-align: right;
}
.cellinput ::v-deep input {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.cellinput ::v-deep .v-text-field__suffix {
  color: var(--muted);
  font-size: 12px;
}
.cellinput ::v-deep fieldset {
  border-color: transparent !important;
}
.cellinput ::v-deep .v-input--is-focused .v-input__slot {
  background: var(--surface-3) !important;
  box-shadow: inset 0 0 0 2px var(--info) !important;
}
.cellinput ::v-deep .v-input--is-focused fieldset {
  border-color: var(--info) !important;
}
.cellinput ::v-deep .error--text .v-input__slot {
  box-shadow: inset 0 0 0 1px var(--error) !important;
}

/* Empty state */
.emptylines {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  border: 1.5px dashed var(--line-strong);
  border-radius: var(--r-lg);
  background: var(--surface);
  padding: 40px 20px;
  margin-bottom: 16px;
}
.emptylines__text {
  color: var(--muted);
  font-size: 14px;
}

/* Two-up */
.twoup {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  align-items: start;
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 16px;
}
.card__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin-bottom: 10px;
}
.summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 7px 0;
  color: var(--ink-soft);
}
.summary__row .mono {
  font-weight: 500;
}
.summary__row--neg {
  color: var(--muted);
}
.summary__row--grand {
  border-top: 1px solid var(--line);
  margin-top: 4px;
  padding-top: 12px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
}
.summary__row--grand .mono {
  font-weight: 700;
}

/* Sticky action bar */
.actionbar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: 0 -2px 6px rgba(24, 24, 27, 0.07), 0 -6px 18px rgba(24, 24, 27, 0.05);
  padding: 12px 16px;
  margin-top: 16px;
}
.actionbar__total {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.actionbar__total-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--muted);
}
.actionbar__total-amt {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}
.actionbar__spacer {
  flex: 1;
}

@media (max-width: 960px) {
  .twoup {
    grid-template-columns: 1fr;
  }
  .metaband {
    grid-template-columns: repeat(1, 1fr);
  }
  .metaband__cell--vendor {
    grid-column: span 1;
  }
}
.po-preview {
  overflow: hidden;
  position: relative;
  border-radius: 8px;
}
.po-preview__head {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  padding: 8px;
}
.po-preview__img {
  display: block;
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>

<style>
.switch-container .v-input--selection-controls {
  margin-top: 5px !important;
}
</style>
